import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, Trash2, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loading } from '../../components/Loading';
import { ImageUploader } from '../../components/ImageUploader';
import { getLogos, addLogo, updateLogo, deleteLogo } from '../../utils/firestoreService';

export const AdminLogos = () => {
  const { user } = useAuth();
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    imageUrl: '',
    order: 0,
  });

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    setLoading(true);
    try {
      const data = await getLogos();
      setLogos(data);
    } catch (error) {
      console.error('Error fetching logos:', error);
      toast.error('Error al cargar los logos');
      setLogos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Debes estar autenticado');
      return;
    }

    if (!formData.imageUrl) {
      toast.error('Debes subir una imagen para el logo');
      return;
    }

    try {
      const submitData = {
        ...formData,
        order: parseInt(formData.order) || 0,
      };

      if (editingLogo) {
        await updateLogo(editingLogo.id, submitData);
        toast.success('Logo actualizado correctamente');
      } else {
        await addLogo(submitData);
        toast.success('Logo creado correctamente');
      }

      setDialogOpen(false);
      resetForm();
      fetchLogos();
    } catch (error) {
      console.error('Error saving logo:', error);
      toast.error('Error al guardar el logo. Verifica las reglas de Firestore.');
    }
  };

  const handleDelete = async (logoId) => {
    if (!user) {
      toast.error('Debes estar autenticado');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar este logo?')) return;

    try {
      await deleteLogo(logoId);
      toast.success('Logo eliminado correctamente');
      fetchLogos();
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error('Error al eliminar el logo');
    }
  };

  const openEditDialog = (logo) => {
    setEditingLogo(logo);
    setFormData({
      name: logo.name,
      imageUrl: logo.imageUrl || '',
      order: logo.order || 0,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLogo(null);
    setFormData({
      name: '',
      imageUrl: '',
      order: 0,
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-fredoka font-bold text-[#1A1A2E]" data-testid="admin-logos-title">
          Logos de Marcas
        </h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <button
              className="bg-[#00C2CB] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#00C2CB]/90 transition-all flex items-center gap-2"
              data-testid="add-logo-button"
            >
              <Plus className="w-5 h-5" />
              Nuevo Logo
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-fredoka text-2xl">
                {editingLogo ? 'Editar Logo' : 'Nuevo Logo'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre de la Marca *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="logo-name-input"
                />
              </div>
              <div>
                <Label htmlFor="order">Orden (Número) *</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  required
                  data-testid="logo-order-input"
                />
                <p className="text-xs text-[#9CA3AF] mt-1 font-quicksand">
                  Los logos se ordenarán de menor a mayor.
                </p>
              </div>
              <div>
                <Label htmlFor="imageUrl">Imagen del Logo *</Label>
                <ImageUploader
                  currentImage={formData.imageUrl}
                  onImageUploaded={(url) => setFormData({ ...formData, imageUrl: url })}
                />
                <p className="text-xs text-[#9CA3AF] mt-2 font-quicksand">
                  La imagen se subirá a Firebase Storage
                </p>
              </div>
              <button
                type="submit"
                className="w-full bg-[#FF6B00] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#FF6B00]/90 transition-all"
                data-testid="logo-submit-button"
              >
                {editingLogo ? 'Actualizar' : 'Crear'} Logo
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {logos.length === 0 && !loading ? (
          <div className="col-span-full text-center py-20 px-4 bg-white rounded-2xl shadow-lg">
            <ImageIcon className="w-16 h-16 mx-auto mb-4 text-[#9CA3AF]" />
            <h3 className="text-xl font-fredoka font-bold text-[#1A1A2E] mb-2">
              No hay logos aún
            </h3>
            <p className="text-[#4A4A6A] font-quicksand mb-6">
              Agrega los logos de las marcas que vendes (ej. Lego, Mattel, Hasbro).
            </p>
            <button
              onClick={() => setDialogOpen(true)}
              className="bg-[#00C2CB] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#00C2CB]/90 transition-all"
              data-testid="empty-state-add-logo"
            >
              Agregar Primer Logo
            </button>
          </div>
        ) : (
          logos.map((logo) => (
          <div
            key={logo.id}
            className="bg-white rounded-2xl p-6 shadow-lg flex flex-col items-center relative group"
            data-testid={`logo-card-${logo.id}`}
          >
            <div className="absolute top-2 right-2 bg-slate-100 text-slate-500 text-xs font-bold px-2 py-1 rounded-md">
              Orden: {logo.order}
            </div>
            <div className="w-full h-32 flex items-center justify-center mb-4">
              {logo.imageUrl ? (
                <img src={logo.imageUrl} alt={logo.name} className="max-w-full max-h-full object-contain" />
              ) : (
                <div className="w-full h-full bg-slate-100 rounded-xl flex items-center justify-center">
                  <span className="text-slate-400 font-quicksand">Sin imagen</span>
                </div>
              )}
            </div>
            <h3 className="font-fredoka font-bold text-lg text-[#1A1A2E] mb-4 text-center">{logo.name}</h3>
            <div className="flex gap-2 w-full mt-auto">
              <button
                onClick={() => openEditDialog(logo)}
                className="flex-1 px-4 py-2 text-[#00C2CB] border border-[#00C2CB] hover:bg-[#00C2CB]/10 rounded-full font-quicksand font-semibold transition-all text-sm flex items-center justify-center gap-1"
                data-testid={`edit-logo-${logo.id}`}
              >
                <Edit2 className="w-4 h-4" />
                Editar
              </button>
              <button
                onClick={() => handleDelete(logo.id)}
                className="flex-1 px-4 py-2 text-red-600 border border-red-600 hover:bg-red-50 rounded-full font-quicksand font-semibold transition-all text-sm flex items-center justify-center gap-1"
                data-testid={`delete-logo-${logo.id}`}
              >
                <Trash2 className="w-4 h-4" />
                Borrar
              </button>
            </div>
          </div>
        )))}
      </div>
    </div>
  );
};
